import Vue from 'vue';

export const lyricSizeOptions = [
  { value:'tiny', label:'极小' }, { value:'extra-small', label:'较小' }, { value:'small', label:'小' },
  { value:'medium', label:'标准' }, { value:'large', label:'大' }, { value:'extra-large', label:'较大' }, { value:'huge', label:'特大' },
];
const defaults = { original:true, translation:true, roman:true, blur:true, scale:true, blurStrength:1, scaleStrength:1, lineHeight:1.2, spring:true, swap:false, size:'medium', weight:0, font:'', spacing:'normal', fadeWidth:0.5, remaining:true };
export type LyricDisplayKey = keyof typeof defaults;
// These are the official renderer's persisted atoms. Both the host settings
// and the iframe menus read/write these keys; no parallel preference is kept.
const keys: Record<LyricDisplayKey,string> = {
  original:'amll-react-full.enableLyricOriginalLine', translation:'amll-react-full.enableLyricTranslationLine',
  roman:'amll-react-full.enableLyricRomanLine', blur:'amll-react-full.enableLyricLineBlurEffect',
  scale:'amll-react-full.enableLyricLineScaleEffect', spring:'amll-react-full.enableLyricLineSpringAnimation',
  blurStrength:'amll-react-full.lyricLineBlurStrength', scaleStrength:'amll-react-full.lyricLineScaleStrength', lineHeight:'amll-react-full.lyricLineHeight',
  swap:'amll-react-full.enableLyricSwapTransRomanLine', size:'amll-react-full.lyricSizePreset', weight:'amll-react-full.lyricFontWeight',
  font:'amll-react-full.lyricFontFamily', spacing:'amll-react-full.lyricLetterSpacing',
  fadeWidth:'amll-react-full.lyricWordFadeWidth', remaining:'amll-react-full.showRemainingTime',
};
const names = Object.keys(defaults) as LyricDisplayKey[];
function valid(name: LyricDisplayKey, value: unknown) {
  if (name === 'size') return lyricSizeOptions.some(option => option.value === value);
  // The official font page accepts 0 (default) through 1000, including values
  // outside the host's presets. Preserve that same persisted value here.
  if (name === 'weight') return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1000;
  if (name === 'font') return typeof value === 'string' && value.length <= 200 && !/[\u0000-\u001f]/.test(value);
  if (name === 'spacing') return typeof value === 'string' && (['normal','0'].includes(value) || /^-?(?:\d+\.?\d*|\.\d+)(?:px|em|rem)$/.test(value));
  if (name === 'fadeWidth') return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10;
  if (name === 'blurStrength' || name === 'scaleStrength') return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
  if (name === 'lineHeight') return typeof value === 'number' && Number.isFinite(value) && value >= 1 && value <= 2;
  return typeof value === 'boolean';
}
export function createLyricDisplayStore(storage: () => Pick<Storage,'getItem'|'setItem'|'removeItem'> | undefined) {
  const read = (name: LyricDisplayKey) => {
    try {
      let value = JSON.parse(storage()?.getItem(keys[name]) || 'null');
      if (name === 'weight' && typeof value === 'string') value = value === 'bold' ? 700 : value === 'normal' ? 400 : Number(value);
      return valid(name,value) ? value : defaults[name];
    }
    catch { return defaults[name]; }
  };
  const state = Vue.observable({ ...defaults });
  const sync = (key: string | null = null) => {
    for (const name of names) if (key === null || keys[name] === key) Vue.set(state,name,read(name));
  };
  sync();
  return { state, sync,
    set(name: LyricDisplayKey, value: unknown) {
      if (!Object.hasOwn(keys,name) || !valid(name,value)) return false;
      try { const disk = storage(); if (!disk) return false; disk.setItem(keys[name],JSON.stringify(value)); Vue.set(state,name,value); return true; }
      catch { return false; }
    },
    reset() {
      const before = new Map<string,string|null>();
      try {
        const disk = storage(); if (!disk) return false;
        for (const name of names) before.set(keys[name],disk.getItem(keys[name]));
        for (const name of names) disk.removeItem(keys[name]);
        sync(); return true;
      } catch {
        for (const [key,value] of before) try { if (value === null) storage()?.removeItem(key); else storage()?.setItem(key,value); } catch { /* Re-read the actual surviving preferences below. */ }
        sync(); return false;
      }
    },
  };
}
const store = createLyricDisplayStore(() => typeof localStorage === 'undefined' ? undefined : localStorage);
export const lyricDisplay = store.state;
export const setLyricDisplay = store.set;
export const resetLyricDisplay = store.reset;
if (typeof window !== 'undefined') window.addEventListener('storage', event => store.sync(event.key));
